import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { AccordionAttachmentsVM, PracticeDetailVM } from 'practice/view-models/practice-detail';
import Form, { TypeFile } from './form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { AttachmentsData } from 'shell/models/practice';
import { HttpResponse } from 'shell/models/http-response';

interface AccordionAttachmentsProps {
  disabled?: boolean;
}

const AccordionAttachmentsComponent = ({
  disabled = false,
}: AccordionAttachmentsProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const params = useParams();

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const insertData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/allegati`, {
    options: { method: 'POST' },
  });

  const deleteData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/allegati`, {
    options: { method: 'DELETE' },
  });

  const formContext = useFormikContext<PracticeDetailVM>();

  const onChange = useEventCallback(
    (
      key: keyof AccordionAttachmentsVM,
      value: AccordionAttachmentsVM[keyof AccordionAttachmentsVM]
    ) => {
      formContext.setFieldValue('attachments', {
        ...(formContext.values.attachments || {}),
        [key]: value,
      });
    }
  );

  const onSave = useEventCallback((file: File) => {
    const reader = new FileReader();

    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};

      const data = {
        codiceEnte: params.codiceEnte || undefined,
        tipoRegistro: params.tipoRegistro || undefined,
        idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
        progressivoPratica: params.progressivoPratica
          ? Number.parseInt(params.progressivoPratica, 10)
          : undefined,
        nomeFile: file.name || undefined,
        idTipoFormato: file.type?.includes('pdf') ? 1 : 99,
        idTipoAllegato: 99,
        contenutoAllegato: (result as string)?.split(',')[1],
        tipoOrigine: 'C',
      };

      (insertData({ data }) as ResponsePromise<HttpResponse<AttachmentsData>>).then((response) => {
        toast.success('Operazione eseguita con successo');

        const newFiles: Array<TypeFile> = formContext.values?.attachments?.files?.slice() || [];

        const files: Array<TypeFile> = [
          {
            ...file,
            raw: result as string,
            idAllegato: response?.data.payload.key?.idAllegato,
            name: response?.data.payload.nomeFile || '',
          },
        ];

        onChange('files', newFiles.concat(files));
      });
    });

    reader.readAsDataURL(file);
  });

  const onTrash = useEventCallback((file: TypeFile | undefined, indexCurr: number | undefined) => {
    (deleteData({ url: `/${file?.idAllegato || ''}` }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      let newFiles: Array<TypeFile> = formContext.values?.attachments?.files?.slice() || [];

      newFiles = newFiles.filter((_, index) => index !== indexCurr) || [];

      onChange('files', newFiles);
    });
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
      ref={ref}
    >
      <AccordionSummary
        aria-controls="accordion-attachments-content"
        id="accordion-attachments-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Allegati</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Form
          onChange={onChange}
          disabled={disabled}
          values={formContext.values?.attachments}
          onSave={onSave}
          onTrash={onTrash}
        />
      </AccordionDetails>
    </Accordion>
  );
};

AccordionAttachmentsComponent.displayName = 'Practice Detail - Form - accordion attachments';

const AccordionAttachments = memo(AccordionAttachmentsComponent);

export default AccordionAttachments;
